import { useEffect, useRef, useState } from "react";

/**
 * Site-wide custom cursor.
 * Two layers: a solid dot following exactly + a larger outline lagging behind with easing.
 * Scales/inverts when hovering interactive elements (button, a, [data-cursor=hover]).
 */
export default function CustomCursor() {
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const [hovering, setHovering] = useState(false);
    const [label, setLabel] = useState("");
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Disable on touch devices
        if (window.matchMedia("(hover: none)").matches) return;

        document.documentElement.classList.add("has-custom-cursor");

        const dot = dotRef.current;
        const ring = ringRef.current;
        let mouseX = window.innerWidth / 2;
        let mouseY = window.innerHeight / 2;
        let ringX = mouseX;
        let ringY = mouseY;

        const onMove = (e) => {
            mouseX = e.clientX;
            mouseY = e.clientY;
            setVisible(true);
            if (dot) {
                dot.style.transform = `translate3d(${mouseX}px, ${mouseY}px, 0) translate(-50%, -50%)`;
            }
        };

        const tick = () => {
            ringX += (mouseX - ringX) * 0.18;
            ringY += (mouseY - ringY) * 0.18;
            if (ring) {
                ring.style.transform = `translate3d(${ringX}px, ${ringY}px, 0) translate(-50%, -50%)`;
            }
            raf = requestAnimationFrame(tick);
        };
        let raf = requestAnimationFrame(tick);

        const onOver = (e) => {
            const t = e.target.closest(
                "a, button, [role='button'], [data-cursor='hover'], input, textarea, label"
            );
            if (t) {
                setHovering(true);
                const customLabel = t.getAttribute("data-cursor-label");
                setLabel(customLabel || "");
            }
        };
        const onOut = (e) => {
            const t = e.target.closest(
                "a, button, [role='button'], [data-cursor='hover'], input, textarea, label"
            );
            if (t) {
                setHovering(false);
                setLabel("");
            }
        };
        const onLeave = () => setVisible(false);

        window.addEventListener("mousemove", onMove);
        document.addEventListener("mouseover", onOver);
        document.addEventListener("mouseout", onOut);
        document.addEventListener("mouseleave", onLeave);

        return () => {
            window.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseover", onOver);
            document.removeEventListener("mouseout", onOut);
            document.removeEventListener("mouseleave", onLeave);
            cancelAnimationFrame(raf);
            document.documentElement.classList.remove("has-custom-cursor");
        };
    }, []);

    return (
        <>
            <div
                ref={dotRef}
                aria-hidden
                className={`fixed top-0 left-0 z-[100] pointer-events-none rounded-full transition-[width,height,background-color,opacity] duration-200 ${
                    hovering
                        ? "w-0 h-0 opacity-0"
                        : "w-1.5 h-1.5 bg-white opacity-90"
                } ${visible ? "" : "opacity-0"}`}
                style={{ willChange: "transform" }}
            />
            <div
                ref={ringRef}
                aria-hidden
                className={`fixed top-0 left-0 z-[100] pointer-events-none flex items-center justify-center font-mono text-[9px] uppercase tracking-[0.2em] transition-[width,height,background-color,color,border-color] duration-300 ease-out ${
                    hovering
                        ? "w-20 h-20 bg-white text-black"
                        : "w-9 h-9 bg-transparent text-white border border-white/40"
                } rounded-full ${visible ? "opacity-100" : "opacity-0"}`}
                style={{ willChange: "transform" }}
            >
                {label}
            </div>
        </>
    );
}
